import { ArrowRight, CheckIcon, RefreshCcw, ThumbsDownIcon, ThumbsUpIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { cn } from '../../../lib/utils.ts';
import type { EntityType } from '../types/entity-types.ts';

type ImproveWritingPreviewProps = {
    onAccept: () => void;
    onReject: () => void;
    onRate?: (rating: 'thumbs_up' | 'thumbs_down') => void;
    improveWriting: () => Promise<void>;
    entityType?: EntityType;
    hasRated?: boolean;
};

export function ImproveWritingPreview({
    onAccept,
    onReject,
    onRate,
    improveWriting,
    hasRated = false,
}: ImproveWritingPreviewProps): ReactNode {
    const { t } = useLocale();

    const handleAlternativeClick = async (): Promise<void> => {
        await improveWriting();
    };

    const handleRate = (rating: 'thumbs_up' | 'thumbs_down'): void => {
        onRate?.(rating);
    };

    return (
        <div className={cn('flex grow flex-col sm:flex-row items-start justify-between gap-x-3')}>
            {/* Left: rating prompt + thumbs or thank you message */}
            <div className="flex items-center gap-2">
                {hasRated ? (
                    <div className="flex items-center gap-2">
                        <CheckIcon className="w-4 h-4 text-green-600" />
                        <Text size="xs">{t('rich_text_editor__feedback_thank_you')}</Text>
                    </div>
                ) : (
                    <>
                        <Text size="xs">{t('rich_text_editor__rate_suggestion')}</Text>
                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                size="icon-s"
                                onClick={() => {
                                    handleRate('thumbs_up');
                                }}
                                icon={ThumbsUpIcon}
                                aria-label={t('rich_text_editor__thumbs_up')}
                            />
                            <Button
                                variant="ghost"
                                size="icon-s"
                                onClick={() => {
                                    handleRate('thumbs_down');
                                }}
                                icon={ThumbsDownIcon}
                                aria-label={t('rich_text_editor__thumbs_down')}
                            />
                        </div>
                    </>
                )}
            </div>
            <div className="flex flex-1 justify-end items-center gap-2">
                <Button
                    variant="ghost"
                    size="sm"
                    className="p-0 sm:px-2"
                    onMouseDown={(e) => {
                        e.preventDefault();
                    }}
                    onClick={onReject}
                >
                    {t('rich_text_editor__cancel')}
                </Button>
                <Button
                    variant="outline"
                    onMouseDown={(e) => {
                        e.preventDefault();
                    }}
                    onClick={handleAlternativeClick}
                    icon={RefreshCcw}
                >
                    {t('rich_text_editor__alternative')}
                </Button>
                <Button
                    variant="primary"
                    onMouseDown={(e) => {
                        e.preventDefault();
                    }}
                    onClick={onAccept}
                    icon={ArrowRight}
                >
                    {t('rich_text_editor__insert')}
                </Button>
            </div>
        </div>
    );
}

export default ImproveWritingPreview;
